import React, { useState } from 'react'
import { Box, Button, Group, Popover, TextInput } from '@mantine/core'
import { ClockIcon } from '@radix-ui/react-icons'

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)
const MINUTES = [0, 30]
const PERIODS = ['AM', 'PM'] as const

type Period = (typeof PERIODS)[number]
type Draft = { hour: number; minute: number; period: Period }

const pad = (n: number): string => String(n).padStart(2, '0')

const from24Hour = (value: string): Draft | null => {
  if (!value) {
    return null
  }
  const [rawHour, minute] = value.split(':').map(Number)
  const period: Period = rawHour >= 12 ? 'PM' : 'AM'
  const hour = rawHour % 12 === 0 ? 12 : rawHour % 12
  return { hour, minute, period }
}

const to24Hour = (draft: Draft): string => {
  const hour = (draft.hour % 12) + (draft.period === 'PM' ? 12 : 0)
  return `${pad(hour)}:${pad(draft.minute)}`
}

const formatDisplay = (value: string): string => {
  const draft = from24Hour(value)
  return draft ? `${draft.hour}:${pad(draft.minute)} ${draft.period}` : ''
}

const TimeColumn = <T extends string | number>({
  values,
  selected,
  onSelect,
  format,
  ariaLabel,
}: {
  values: readonly T[]
  selected: T | null
  onSelect: (value: T) => void
  format: (value: T) => string
  ariaLabel: string
}) => (
  <Box
    role="listbox"
    aria-label={ariaLabel}
    className="h-40 w-16 overflow-y-auto py-1"
  >
    {values.map(value => (
      <Box
        key={value}
        role="option"
        aria-selected={selected === value}
        onClick={() => onSelect(value)}
        className={`cursor-pointer rounded px-2 py-1 text-center text-sm ${
          selected === value ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
        }`}
      >
        {format(value)}
      </Box>
    ))}
  </Box>
)

export const TimePickerInput: React.FC<{
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  ariaLabel: string
  className?: string
}> = ({ value, onChange, placeholder, disabled, ariaLabel, className }) => {
  const [opened, setOpened] = useState(false)
  const [draftHour, setDraftHour] = useState<number | null>(null)
  const [draftMinute, setDraftMinute] = useState<number | null>(null)
  const [draftPeriod, setDraftPeriod] = useState<Period | null>(null)

  const open = () => {
    if (disabled) {
      return
    }
    const current = from24Hour(value)
    setDraftHour(current?.hour ?? null)
    setDraftMinute(current?.minute ?? null)
    setDraftPeriod(current?.period ?? null)
    setOpened(true)
  }

  const confirm = () => {
    if (draftHour === null || draftMinute === null || draftPeriod === null) {
      return
    }
    onChange(
      to24Hour({ hour: draftHour, minute: draftMinute, period: draftPeriod }),
    )
    setOpened(false)
  }

  const clear = () => {
    onChange('')
    setOpened(false)
  }

  return (
    <Popover
      opened={opened}
      onClose={() => setOpened(false)}
      withinPortal
      position="bottom-start"
      shadow="md"
    >
      <Popover.Target>
        <TextInput
          aria-label={ariaLabel}
          placeholder={placeholder}
          value={formatDisplay(value)}
          readOnly
          disabled={disabled}
          onClick={open}
          rightSection={<ClockIcon className="text-gray-400" />}
          styles={{ input: { cursor: disabled ? 'default' : 'pointer' } }}
          className={className}
        />
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Group noWrap spacing={0} className="divide-x divide-gray-200">
          <TimeColumn
            ariaLabel={`${ariaLabel} hour`}
            values={HOURS}
            format={pad}
            selected={draftHour}
            onSelect={setDraftHour}
          />
          <TimeColumn
            ariaLabel={`${ariaLabel} minute`}
            values={MINUTES}
            format={pad}
            selected={draftMinute}
            onSelect={setDraftMinute}
          />
          <TimeColumn
            ariaLabel={`${ariaLabel} period`}
            values={PERIODS}
            format={period => period}
            selected={draftPeriod}
            onSelect={setDraftPeriod}
          />
        </Group>
        <Group position="right" p="xs" className="border-t border-gray-200">
          <Button variant="subtle" size="xs" color="gray" onClick={clear}>
            Clear
          </Button>
          <Button
            size="xs"
            onClick={confirm}
            disabled={
              draftHour === null || draftMinute === null || draftPeriod === null
            }
          >
            OK
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  )
}
